import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import type { ComponentType, PropsWithChildren } from 'react';

import Header from './components/Header';
import Footer from './components/Footer';
import AnalyticsLoader from './components/AnalyticsLoader';
import CookieConsentBanner from './components/CookieConsentBanner';
import { projects } from './data/projects';
import './i18n'; // Import i18n configuration
import { useTranslation } from 'react-i18next';
import Home from './components/Home';
import Seo from './components/Seo';
import { getPersonJsonLd } from './config/seo';

// Lazy-loaded route components for code splitting
const About = lazy(() => import('./components/About'));
const Projects = lazy(() => import('./components/Projects'));
const Skills = lazy(() => import('./components/Skills'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const Contact = lazy(() => import('./components/Contact'));
const ProjectDetails = lazy(() => import('./components/ProjectDetails'));
const NotFound = lazy(() => import('./components/NotFound'));
const LegacyFrontendRescuePage = lazy(() => import('./pages/services/LegacyFrontendRescuePage'));
const HealthCheckFlowPage = lazy(() => import('./pages/health-check/HealthCheckFlowPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const CookieSettingsPage = lazy(() => import('./pages/CookieSettingsPage'));

interface AppProps {
  RouterComponent?: ComponentType<PropsWithChildren>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

/** Renders Seo with Person JSON-LD on homepage; must be inside Router. */
function SeoWithHomeJsonLd() {
  const { pathname } = useLocation();
  return <Seo jsonLd={pathname === '/' ? getPersonJsonLd() : undefined} />;
}

function App({ RouterComponent = Router }: AppProps) {
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(() => {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // If no saved preference, use system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Separate function for manual theme toggle
  const handleManualThemeChange = useCallback((newTheme: boolean) => {
    setDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    // Only update theme class, don't store preference
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      if (!localStorage.getItem('theme')) {
        setDarkMode(e.matches);
      }
    };

    // Add listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  const appContent = (
    <>
      <SeoWithHomeJsonLd />
      <AnalyticsLoader />
      <CookieConsentBanner />
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">{t('common.skipToMain')}</a>
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 transition-colors duration-200 page-ambient">
        <Header darkMode={darkMode} setDarkMode={handleManualThemeChange} />
        <main id="main-content" role="main" className="grow">
          <Suspense fallback={<div className="container py-20 text-center">{t('common.loading')}</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetails projects={projects} />} />
              <Route path='/skills' element={<Skills />} />
              <Route path='/testimonials' element={<Testimonials />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/cookie-settings" element={<CookieSettingsPage />} />
              <Route path="/services/legacy-frontend-rescue" element={<LegacyFrontendRescuePage />} />
              <Route path="/health-check" element={<HealthCheckFlowPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </>
  );

  return RouterComponent ? (
    <RouterComponent>
      <ScrollToTop />
      {appContent}
    </RouterComponent>
  ) : appContent;
}

export default App;
